/**
 * The app registry as the chrome reads it: the row shape, the path matcher, the suite model, the
 * switcher groups and the origin of each tile (kernel apps, WS-13 section 3.2).
 *
 * Every function except `destinationOf` moves from the shell's `logic/nav-utils.ts` and
 * `logic/app-loader-utils.ts` with its behaviour unchanged. Four deltas apply:
 * 1. The registry type is {@link ChromeApp}, a structural subset of the shell's
 *    `AppRegistryEntry`, so a shell row passes as it is. `matchAppByPath` and
 *    `groupAppsByCategory` are generic and hand back the caller's row type.
 * 2. `createHomeAppEntry` and `createAdminAppEntry` return a {@link ChromeApp}, with no
 *    `appUuid`, bundle or permission keys.
 * 3. The admin tile's `defaultRoute` stays `appHref(mountPath, defaultRoute)` of the hosted
 *    `ADMIN_APP_ID` row (R-63(1) of the shell).
 * 4. `buildSuiteGroups` refuses a `requiresPermission` that is not a non-empty string, and reads
 *    a `manifest.shell` that is not an array as no entries (R-119).
 * Every function is pure.
 */
import { hasPermission } from '@zion/sdk-contracts';
import type { ShellEntryDeclaration } from '@zion/sdk-cp-dp-contracts';
import { ADMIN_APP_ID } from '@zion/sdk-dp-admin-contracts';

import type { ChromeDestination } from './origins.js';
import { appHref, isUnderMount } from './paths.js';

/**
 * One registry row, with the fields the chrome reads. `manifest.mountPath` and
 * `manifest.defaultRoute` are as `GET /v1/apps` stores them, written by the publisher, so a link
 * built from them goes through `chromeHref`.
 */
export interface ChromeApp {
  readonly appId: string;
  readonly manifest: {
    readonly title: string;
    readonly icon: string;
    readonly mountPath: string;
    readonly defaultRoute: string;
    readonly category: string;
    readonly shell?: readonly ShellEntryDeclaration[];
  };
}

/**
 * One chooser-tagged surface of a suite. A suite with at most one surface draws as one tile; a
 * suite with two or more draws as a section.
 */
export interface Surface {
  surfaceId: string;
  label: string;
  icon?: string;
  mountPath: string;
  defaultRoute: string;
  requiresPermission?: string;
}

/** The chooser-tagged surfaces of one app. */
export interface SuiteGroup {
  app: {
    appId: string;
    displayName: string;
    icon: string;
    mountPath: string;
    defaultRoute: string;
    category: string;
  };
  surfaces: Surface[];
}

/**
 * One category group of the app switcher. `apps[i]` and `suites[i]` are parallel: the suite at
 * index `i` holds the chooser surfaces of the app at index `i`. The Home and Administration tiles
 * and an app with no chooser entry have an empty `surfaces` list, and the header draws one tile.
 */
export interface AppSwitcherGroup {
  readonly category: string;
  readonly label: string;
  readonly apps: readonly ChromeApp[];
  readonly suites: readonly SuiteGroup[];
}

/** The sentinel app id of the built-in home page. No registry row and no bundle carry it. */
export const HOME_APP_ID = '__home__';

/**
 * The sentinel tile id of the Administration tile. No registry row and no bundle carry it. It
 * differs from `ADMIN_APP_ID` (`'admin-app'`), the hosted admin app's registry id (R-59(6)). The
 * header's active-app label and its built-in tile branch compare against it.
 */
export const ADMIN_TILE_ID = '__admin__';

/**
 * The registry row with the longest `mountPath` that holds `pathname` on a segment boundary
 * ({@link isUnderMount}), or `null` when no row does.
 *
 * Callers: the header for its active-app label, and the shell's `AppHost` for the bundle it
 * mounts, so the chrome names the app that loaded. The longest mount wins over declaration order,
 * so `/brewblast/show` beats `/brewblast`. There is no fallback to the first row (#2139); `null`
 * is the answer, and the host draws its not-found surface on it.
 */
export function matchAppByPath<T extends ChromeApp>(registry: readonly T[], pathname: string): T | null {
  let best: T | null = null;
  for (const app of registry) {
    const mp = app.manifest.mountPath;
    if (isUnderMount(pathname, mp) && (best === null || mp.length > best.manifest.mountPath.length)) {
      best = app;
    }
  }
  return best;
}

/**
 * `chooser.surfaceId` as a string, and `''` for any other wire value. The type says `string`,
 * and `GET /v1/apps` returns stored manifests that core-api never re-validates, so a legacy or
 * hand-written row can carry another type. One normalisation feeds both the sort and the
 * projected `Surface.surfaceId` (C-062).
 */
function surfaceIdOf(s: ShellEntryDeclaration): string {
  return typeof s.chooser!.surfaceId === 'string' ? s.chooser!.surfaceId : '';
}

/** True when `v` is a non-null object. A wire value can be `null` where the type says object. */
function isObject(v: unknown): boolean {
  return typeof v === 'object' && v !== null;
}

/**
 * Build one suite group per registry row.
 *
 * For each row:
 * 1. Read `manifest.shell` when it is an array. An absent `shell`, and a wire value that is not
 *    an array (an object, a string, a number), gives the row no surfaces and no throw.
 * 2. Keep each shell entry that is an object and whose `chooser` is an object. A wire `null`, a
 *    string, a number or a boolean goes, as an entry or as its `chooser`, and the sibling entries
 *    stay (C-072).
 * 3. Keep each entry whose `chooser.requiresPermission` is absent, or is a non-empty string that
 *    passes `hasPermission` over `userPermissions`, which honours `*` and `prefix:*` (DEC-A16).
 *    Every other wire value is a refusal for every viewer, `*` included, and never a throw:
 *    `null`, `0`, `false`, `''`, `NaN`, another number, an array and an object (R-119).
 *    `filterNavSections` refuses the same values.
 * 4. Sort by `chooser.order` (absent is 0), then by `surfaceId`. A non-numeric `order` gives an
 *    unspecified position and no throw (C-073).
 * 5. Project each entry to a {@link Surface}. A non-string `label` becomes `''` (C-073), a
 *    non-string `surfaceId` becomes `''` (C-062), and `icon` stays as the wire gives it.
 *
 * @returns One group per row, in registry order, with the app's title as `displayName`.
 */
export function buildSuiteGroups(registry: readonly ChromeApp[], userPermissions: ReadonlySet<string>): SuiteGroup[] {
  const perms = [...userPermissions];
  return registry.map((e) => {
    const shellEntries: readonly ShellEntryDeclaration[] = Array.isArray(e.manifest.shell) ? e.manifest.shell : [];
    const chooserTagged = shellEntries
      .filter((s) => isObject(s) && isObject(s.chooser))
      .filter((s) => {
        const req: unknown = s.chooser!.requiresPermission;
        return req === undefined || (typeof req === 'string' && req !== '' && hasPermission(perms, req));
      });

    const sorted = [...chooserTagged].sort((a, b) => {
      const orderA = a.chooser!.order ?? 0;
      const orderB = b.chooser!.order ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      return surfaceIdOf(a).localeCompare(surfaceIdOf(b));
    });

    const surfaces: Surface[] = sorted.map((s) => ({
      surfaceId: surfaceIdOf(s),
      label: typeof s.chooser!.label === 'string' ? s.chooser!.label : '',
      icon: s.chooser!.icon,
      mountPath: s.mountPath,
      defaultRoute: s.defaultRoute,
      requiresPermission: s.chooser!.requiresPermission,
    }));

    return {
      app: {
        appId: e.appId,
        displayName: e.manifest.title,
        icon: e.manifest.icon,
        mountPath: e.manifest.mountPath,
        defaultRoute: e.manifest.defaultRoute,
        category: e.manifest.category,
      },
      surfaces,
    };
  });
}

/**
 * Group apps by `manifest.category` for the app switcher. The groups sort by category and the
 * apps in a group sort by title, both by code unit. The category is also the group's label.
 */
export function groupAppsByCategory<T extends ChromeApp>(apps: readonly T[]): { category: string; label: string; apps: T[] }[] {
  const groupMap = new Map<string, T[]>();
  for (const app of apps) {
    const key = app.manifest.category;
    let list = groupMap.get(key);
    if (!list) {
      list = [];
      groupMap.set(key, list);
    }
    list.push(app);
  }

  const groups = [...groupMap.entries()].map(([category, groupApps]) => {
    const sorted = [...groupApps].sort((a, b) => (a.manifest.title < b.manifest.title ? -1 : 1));
    return { category, label: category, apps: sorted };
  });
  groups.sort((a, b) => (a.category < b.category ? -1 : 1));
  return groups;
}

/** The Home tile: `HOME_APP_ID`, title `Home`, icon `home`, mount and route `/home`, category `__home__`. */
export function createHomeAppEntry(): ChromeApp {
  return {
    appId: HOME_APP_ID,
    manifest: {
      title: 'Home',
      icon: 'home',
      mountPath: '/home',
      defaultRoute: '/home',
      category: '__home__',
    },
  };
}

/**
 * The Administration tile, or `null` when the registry holds no row whose `appId` is
 * `ADMIN_APP_ID`.
 *
 * The registry is the one source of the tile's route (R-70, R-71). Two states reach `null`: the
 * window before `GET /v1/apps` settles, and a settled registry for a principal that the
 * visibility filter answers without the app. The first row with the id wins, because
 * `GET /v1/apps` keys its rows by `appId`.
 *
 * The tile is `ADMIN_TILE_ID`, title `Administration`, icon `settings`, category `__admin__`. Its
 * `mountPath` is the row's `manifest.mountPath`, and its `defaultRoute` is
 * `appHref(mountPath, defaultRoute)` of the row, because the manifest's route is relative to the
 * mount (R-63(1)).
 */
export function createAdminAppEntry(registry: readonly ChromeApp[]): ChromeApp | null {
  const hosted = registry.find((entry) => entry.appId === ADMIN_APP_ID);
  if (hosted === undefined) return null;
  return {
    appId: ADMIN_TILE_ID,
    manifest: {
      title: 'Administration',
      icon: 'settings',
      mountPath: hosted.manifest.mountPath,
      defaultRoute: appHref(hosted.manifest.mountPath, hosted.manifest.defaultRoute),
      category: '__admin__',
    },
  };
}

function pairSuite(app: ChromeApp, userPermissions: ReadonlySet<string>): SuiteGroup {
  return buildSuiteGroups([app], userPermissions)[0]!;
}

/**
 * The whole app switcher, in order:
 * 1. the Home group (`__home__`, empty label), always;
 * 2. the Administration group (`__admin__`, label `Admin`) when `includeAdmin` is `true` and
 *    {@link createAdminAppEntry} answers a tile;
 * 3. every other row, through {@link groupAppsByCategory}. The `ADMIN_APP_ID` row leaves this
 *    pass, so a viewer that sees the app gets one Administration tile and not two (R-11).
 * Each app pairs with its {@link SuiteGroup}.
 *
 * `includeAdmin` is the caller's view state (`canAccessAdmin` of the viewer's roles). The
 * registry row is the authority for the tile: with the flag and no row, no admin group exists.
 */
export function buildAppSwitcherGroups(
  apps: readonly ChromeApp[],
  includeAdmin: boolean,
  userPermissions: ReadonlySet<string> = new Set(),
): AppSwitcherGroup[] {
  const groups: AppSwitcherGroup[] = [];

  const home = createHomeAppEntry();
  groups.push({ category: '__home__', label: '', apps: [home], suites: [pairSuite(home, userPermissions)] });

  if (includeAdmin) {
    const admin = createAdminAppEntry(apps);
    if (admin !== null) {
      groups.push({ category: '__admin__', label: 'Admin', apps: [admin], suites: [pairSuite(admin, userPermissions)] });
    }
  }

  const installed = apps.filter((app) => app.appId !== ADMIN_APP_ID);
  for (const cat of groupAppsByCategory(installed)) {
    groups.push({
      category: cat.category,
      label: cat.label,
      apps: cat.apps,
      suites: cat.apps.map((a) => pairSuite(a, userPermissions)),
    });
  }
  return groups;
}

/**
 * The origin of an app's tile, with `route` as its path.
 *
 * @returns `{ origin: 'kernel', path: route }` when `app.appId` is `ADMIN_TILE_ID` or
 *   `ADMIN_APP_ID`, because decisions 11 and 12 put Witness's admin app alone on `kernel.<dp>`.
 *   `{ origin: 'shell', path: route }` for every other app, Home included. It does not check
 *   `route`; `chromeHref` does. It never throws.
 */
export function destinationOf(app: ChromeApp, route: string): ChromeDestination {
  const kernel = app.appId === ADMIN_TILE_ID || app.appId === ADMIN_APP_ID;
  return { origin: kernel ? 'kernel' : 'shell', path: route };
}
